/**
 * React Query Configuration
 * ==========================
 *
 * Centralized setup for @tanstack/react-query
 *
 * Features:
 * - Automatic caching and revalidation
 * - Request deduplication
 * - Background refetching
 * - Optimistic updates
 * - Error retry logic
 */

import { QueryClient } from '@tanstack/react-query';

import { notify } from '@/lib/notify';

/**
 * Create and configure QueryClient
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: How long data is considered fresh (5 minutes)
      staleTime: 5 * 60 * 1000,

      // Cache time: How long unused data stays in cache (10 minutes)
      gcTime: 10 * 60 * 1000,

      // Refetch on window focus (good for keeping data fresh)
      refetchOnWindowFocus: true,

      // Refetch on reconnect (good for offline scenarios)
      refetchOnReconnect: true,

      // Retry failed requests (3 times with exponential backoff)
      retry: (failureCount, error: any) => {
        // Don't retry on 401 (auth errors) or 404 (not found)
        if (error?.status === 401 || error?.status === 404) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // Retry delay (exponential backoff)
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },

    mutations: {
      // Retry mutations once on failure
      retry: 1,

      // Safety-net error handler for mutations. Component-level
      // `onError` callbacks still run first (and can use their own
      // `notify.error(...)` with caller-specific copy) - this only
      // catches mutations that forgot to handle their own errors so
      // failures aren't silent.
      //
      // Queries intentionally have no default `onError`: a background
      // poll that fails shouldn't toast, since the UI already shows
      // skeletons / per-panel `ArtifactGate` failure states.
      onError: (error: unknown) => {
        console.error('Mutation error:', error);
        notify.error(error);
      },
    },
  },
});

/**
 * Query Keys
 * ===========
 *
 * Centralized query key factory for type safety and consistency
 */
export const queryKeys = {
  // User queries
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    preferences: () => [...queryKeys.user.all, 'preferences'] as const,
    /** Account-level default lesson flow + depth (`/users/me/lesson-settings`). */
    lessonSettings: () => [...queryKeys.user.all, 'lessonSettings'] as const,
  },

  // Lessons queries
  // All lesson-scoped queries hang off ['lessons'], so
  // `invalidateQueries({ queryKey: queryKeys.lessons.all })` actually reaches
  // them.
  //
  // It did not before. Per-artifact queries were written as string literals
  // with their own singular, hyphenated roots: "lesson-metadata",
  // "lesson-video", "lesson-pipeline-status" (8 sites) and six more. None of
  // those shared a prefix with ["lessons"], so `invalidateLessonQueries()`
  // matched none of them, and `useVersions.ts` carried a comment noting the
  // hazard rather than fixing it.
  //
  // Worse, one resource was cached twice under two spellings: TextRenderer
  // called the factory's `metadata(id)` while VideoRenderer wrote the
  // "lesson-metadata" literal. Two entries for one payload, and invalidating
  // either left the other stale. Two files also disagreed on whether the id
  // was `lessonId` or `lessonId || ''`, forking the cache whenever it was
  // undefined.
  //
  // Add new lesson queries HERE. Do not write a literal.
  lessons: {
    all: ['lessons'] as const,
    list: (filters?: any) =>
      [...queryKeys.lessons.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.lessons.all, 'detail', id] as const,
    metadata: (id: string) =>
      [...queryKeys.lessons.all, 'metadata', id] as const,
    status: (id: string) => [...queryKeys.lessons.all, 'status', id] as const,
    tags: () => [...queryKeys.lessons.all, 'tags'] as const,

    // Per-lesson artifacts and derived views.
    pipelineStatus: (id: string) =>
      [...queryKeys.lessons.all, 'pipeline-status', id] as const,
    highlights: (id: string) =>
      [...queryKeys.lessons.all, 'highlights', id] as const,
    text: (id: string) => [...queryKeys.lessons.all, 'text', id] as const,
    mindmap: (id: string) => [...queryKeys.lessons.all, 'mindmap', id] as const,
    buildPreview: (id: string) =>
      [...queryKeys.lessons.all, 'build-preview', id] as const,
    buildPlan: (id: string) =>
      [...queryKeys.lessons.all, 'build-plan', id] as const,
    video: (id: string) => [...queryKeys.lessons.all, 'video', id] as const,
    // Podcasts are per section: the list, and one section's podcast.
    podcasts: (id: string) =>
      [...queryKeys.lessons.all, 'podcasts', id] as const,
    podcast: (id: string, section: number) =>
      [...queryKeys.lessons.all, 'podcasts', id, section] as const,
    context: (id: string) => [...queryKeys.lessons.all, 'context', id] as const,
    progress: (id: string) =>
      [...queryKeys.lessons.all, 'progress', id] as const,
    classifiedChat: (id: string) =>
      [...queryKeys.lessons.all, 'classified-chat', id] as const,
  },

  // Suggested topics (home page)
  topics: {
    all: ['topics'] as const,
    suggested: (count?: number) =>
      [...queryKeys.topics.all, 'suggested', count] as const,
  },

  // Discovery
  discover: {
    all: ['discover'] as const,
    courses: (filters?: any) =>
      [...queryKeys.discover.all, 'courses', filters] as const,
    course: (id: string) => [...queryKeys.discover.all, 'course', id] as const,
  },

  // Mindspace keys deliberately do NOT live here.
  //
  // This block declared a third `all: ['mindspace']` alongside `mindspaceKeys`
  // (hooks/mindspace/useMindspace.ts) and `revisionKeys`
  // (hooks/mindspace/useRevision.ts) — one namespace owned by three factories
  // in three files, none referencing the others. It had zero consumers, so it
  // was pure collision surface. The two hook-local factories are the real
  // owners; `revisionKeys` is now nested at ['mindspace','revision'] so its
  // invalidations stop wiping the graph and the tag-option lists.

  // Chat
  chat: {
    all: ['chat'] as const,
    history: (lessonId: string) =>
      [...queryKeys.chat.all, 'history', lessonId] as const,
  },

  // Wellbeing. Keyed by user, because the focus log is per user and a
  // shared computer must never read someone else's.
  wellbeing: {
    all: ['wellbeing'] as const,
    focusBlocks: (userId: string) =>
      [...queryKeys.wellbeing.all, 'focus-blocks', userId] as const,
    thought: (date: string) =>
      [...queryKeys.wellbeing.all, 'thought', date] as const,
  },

  // Billing / Stripe
  billing: {
    all: ['billing'] as const,
    plans: () => [...queryKeys.billing.all, 'plans'] as const,
    subscription: () => [...queryKeys.billing.all, 'subscription'] as const,
    invoices: (limit?: number) =>
      [...queryKeys.billing.all, 'invoices', limit] as const,
  },
};

/**
 * Helper: Invalidate all user-related queries
 */
export const invalidateUserQueries = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.user.all });
};

/**
 * Helper: Invalidate all lesson-related queries
 */
export const invalidateLessonQueries = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all });
};
